// ContractDetailsDrawer.jsx
// Right-side Contract Details Drawer matching reference screenshot

import React, { useState } from "react";
import {
  X,
  CheckCircle2,
  Download,
  RotateCw,
  FileText,
  FileCode,
  Building2,
  Calendar,
  Clock,
  UserCheck,
} from "lucide-react";
import RightDrawer from "../../shared/components/RightDrawer";
import StatusBadge from "../../shared/components/StatusBadge";
import ContractHistory from "./ContractHistory";
import ContractDocuments from "./ContractDocuments";

const ContractDetailsDrawer = ({
  isOpen = false,
  onClose,
  contract,
  onRenew,
  onDownload,
}) => {
  const [activeTab, setActiveTab] = useState("overview");

  if (!contract) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const attachments = contract.attachments || [
    {
      id: "att-1",
      name: "Employment_Contract.pdf",
      size: "245 KB",
      type: "pdf",
    },
    {
      id: "att-2",
      name: "Contract_Terms.docx",
      size: "128 KB",
      type: "docx",
    },
  ];

  return (
    <RightDrawer
      open={isOpen}
      onClose={onClose}
      width="540px"
      footer={
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onRenew?.(contract)}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50/50 px-4 py-2.5 text-xs font-semibold text-blue-700 hover:bg-blue-100/70 transition"
          >
            <RotateCw size={15} />
            <span>Renew Contract</span>
          </button>
          <button
            type="button"
            onClick={() => onDownload?.(contract)}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#147db5] px-4 py-2.5 text-xs font-semibold text-white hover:bg-[#116d9d] shadow-sm transition"
          >
            <Download size={15} />
            <span>View / Download</span>
          </button>
        </div>
      }
    >
      <div className="p-6 space-y-6">
        {/* Top Header Employee Section */}
        <div className="flex items-start gap-4">
          <img
            src={
              contract.avatar ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                contract.employee_name || "User"
              )}&background=147db5&color=fff`
            }
            alt={contract.employee_name}
            className="h-14 w-14 rounded-full object-cover border-2 border-slate-100 shadow-sm"
          />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-900 truncate">
                {contract.employee_name}
              </h2>
              <StatusBadge status={contract.status} />
            </div>

            <p className="mt-0.5 text-xs font-medium text-slate-500">
              {contract.employee_id || "EMP-1001"} • {contract.job_title || "Senior Software Engineer"}
            </p>

            <p className="mt-0.5 text-xs text-slate-400">
              {contract.department || "Engineering Department"}
            </p>
          </div>
        </div>

        {/* Tabs header */}
        <div className="border-b border-slate-200">
          <div className="flex gap-8 text-xs font-semibold">
            <button
              type="button"
              onClick={() => setActiveTab("overview")}
              className={`pb-3 transition-colors relative ${activeTab === "overview"
                  ? "text-[#147db5] font-bold"
                  : "text-slate-500 hover:text-slate-800"
                }`}
            >
              Overview
              {activeTab === "overview" && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#147db5] rounded-full" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("history")}
              className={`pb-3 transition-colors relative ${activeTab === "history"
                  ? "text-[#147db5] font-bold"
                  : "text-slate-500 hover:text-slate-800"
                }`}
            >
              History
              {activeTab === "history" && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#147db5] rounded-full" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("documents")}
              className={`pb-3 transition-colors relative ${activeTab === "documents"
                  ? "text-[#147db5] font-bold"
                  : "text-slate-500 hover:text-slate-800"
                }`}
            >
              Documents
              {activeTab === "documents" && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#147db5] rounded-full" />
              )}
            </button>
          </div>
        </div>

        {/* Tab Contents */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Soft green bordered Current Contract Card */}
            <div className="relative rounded-2xl border border-emerald-300/80 bg-emerald-50/30 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-bold text-slate-900 tracking-tight">
                  Current Contract
                </h3>
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <CheckCircle2 size={16} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-y-3 text-xs">
                <div>
                  <span className="text-slate-500 font-medium block">Contract Type</span>
                  <span className="font-semibold text-slate-800 mt-0.5 block">
                    {contract.contract_type || "Permanent"}
                  </span>
                </div>

                <div>
                  <span className="text-slate-500 font-medium block">Start Date</span>
                  <span className="font-semibold text-slate-800 mt-0.5 block">
                    {formatDate(contract.start_date)}
                  </span>
                </div>

                <div>
                  <span className="text-slate-500 font-medium block">End Date</span>
                  <span className="font-semibold text-slate-800 mt-0.5 block">
                    {formatDate(contract.end_date)}
                  </span>
                </div>

                <div>
                  <span className="text-slate-500 font-medium block">Duration</span>
                  <span className="font-semibold text-slate-800 mt-0.5 block">
                    {contract.duration || "—"}
                  </span>
                </div>

                <div>
                  <span className="text-slate-500 font-medium block">Status</span>
                  <span className="font-semibold text-emerald-600 mt-0.5 block">
                    {contract.status || "Active"}
                  </span>
                </div>
              </div>
            </div>

            {/* Contract Information Section */}
            <div>
              <h3 className="text-xs font-bold text-slate-900 tracking-tight mb-3">
                Contract Information
              </h3>

              <div className="divide-y divide-slate-100 rounded-xl border border-slate-200/80 bg-white text-xs">
                <div className="flex justify-between py-2.5 px-4">
                  <span className="text-slate-500 font-medium">Employment Type</span>
                  <span className="font-semibold text-slate-800">
                    {contract.employment_type || "—"}
                  </span>
                </div>

                <div className="flex justify-between py-2.5 px-4">
                  <span className="text-slate-500 font-medium">Work Schedule</span>
                  <span className="font-semibold text-slate-800">
                    {contract.work_schedule || "—"}
                  </span>
                </div>

                <div className="flex justify-between py-2.5 px-4">
                  <span className="text-slate-500 font-medium">Probation Period</span>
                  <span className="font-semibold text-slate-800">
                    {contract.probation_period || "—"}
                  </span>
                </div>

                <div className="flex justify-between py-2.5 px-4">
                  <span className="text-slate-500 font-medium">Probation End Date</span>
                  <span className="font-semibold text-slate-800">
                    {formatDate(contract.probation_end_date)}
                  </span>
                </div>

                <div className="flex justify-between py-2.5 px-4">
                  <span className="text-slate-500 font-medium">Renewal Notice Period</span>
                  <span className="font-semibold text-slate-800">
                    {contract.renewal_notice_period || "—"}
                  </span>
                </div>

                <div className="flex justify-between py-2.5 px-4">
                  <span className="text-slate-500 font-medium">Created By</span>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-800">
                      {contract.created_by || "—"}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between py-2.5 px-4 border-b-0">
                  <span className="text-slate-500 font-medium">Created Date</span>
                  <span className="font-semibold text-slate-800">
                    {formatDate(contract.created_date)}
                  </span>
                </div>
              </div>
            </div>

            {/* Attachments Section */}
            <div>
              <h3 className="text-xs font-bold text-slate-900 tracking-tight mb-3">
                Attachments ({attachments.length})
              </h3>

              <div className="space-y-2.5">
                {attachments.map((file, idx) => {
                  const isPdf = file.name?.endsWith(".pdf");
                  return (
                    <div
                      key={file.id || idx}
                      className="flex items-center justify-between rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm hover:border-slate-300 transition"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex h-9 w-9 items-center justify-center rounded-lg ${isPdf ? "bg-rose-50 text-rose-600" : "bg-blue-50 text-blue-600"
                            }`}
                        >
                          {isPdf ? <FileText size={18} /> : <FileCode size={18} />}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-800">
                            {file.name}
                          </p>
                          <p className="text-[10px] text-slate-400">{file.size}</p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => onDownload?.(contract)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition"
                      >
                        <Download size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === "history" && (
          <ContractHistory history={contract.history} />
        )}

        {activeTab === "documents" && (
          <ContractDocuments documents={contract.documents} />
        )}
      </div>
    </RightDrawer>
  );
};

export default ContractDetailsDrawer;
